import type { Rect } from '@flobi/artwork-core';
import type { Camera } from '@flobi/artwork-core/render';
import { fitCamera, zoomAt } from '../canvas/camera';

export const SCENARIOS = {
  'zoom-all': 'Everything visible, zoom breathing ±10%',
  'pan-1x': 'Panning at 100% zoom',
  'drag': 'Dragging one object at 100% zoom',
  'zoom-in': 'Zooming from the whole page to 800%',
} as const;

export type Scenario = keyof typeof SCENARIOS;

export interface ScenarioContext {
  readonly bounds: Rect;
  readonly view: { width: number; height: number };
  /** Moves a test object to a world position, as a drag would. */
  readonly moveObject: (x: number, y: number) => void;
  /** Where the dragged object starts, in world units. */
  readonly dragOrigin: { x: number; y: number };
  readonly durationMs: number;
}

/** Returns a function that sets the camera (and edits, for drag) for a time since the start. */
export function scenarioStep(name: Scenario, ctx: ScenarioContext): (elapsed: number) => Camera {
  const { bounds, view } = ctx;
  const fit = fitCamera(bounds, view.width, view.height);
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  switch (name) {
    case 'zoom-all':
      return (t) => zoomAt(fit, view.width / 2, view.height / 2, 1 + 0.1 * Math.sin(t / 300));
    case 'pan-1x':
      return (t) => ({
        x: centerX - view.width / 2 + 800 * Math.sin(t / 500),
        y: centerY - view.height / 2 + 500 * Math.cos(t / 700),
        zoom: 1,
      });
    case 'drag': {
      const { x, y } = ctx.dragOrigin;
      const camera = { x: x - view.width / 2, y: y - view.height / 2, zoom: 1 };
      return (t) => {
        ctx.moveObject(x + 80 * Math.cos(t / 250), y + 80 * Math.sin(t / 250));
        return camera;
      };
    }
    case 'zoom-in': {
      const target = 8;
      return (t) => {
        const k = Math.min(1, t / ctx.durationMs);
        const zoom = fit.zoom * Math.pow(target / fit.zoom, k);
        return zoomAt(fit, view.width / 2, view.height / 2, zoom / fit.zoom);
      };
    }
  }
}

export interface FrameSample {
  readonly interval: number;
  /** Time inside renderer.draw, including tile drawing and the GPU submit. */
  readonly drawMs: number;
  readonly drawnUnits: number;
  readonly renderMs: number;
  readonly pendingTiles: number;
  readonly renderedTiles: number;
  readonly level: number;
  readonly at: number;
}

export interface MeasureResult {
  readonly scenario: Scenario;
  readonly frames: number;
  readonly fps: number;
  readonly medianMs: number;
  readonly p95Ms: number;
  readonly worstMs: number;
  readonly drawMedianMs: number;
  readonly drawP95Ms: number;
  readonly drawWorstMs: number;
  readonly tilesDrawn: number;
  readonly objectsDrawn: number;
  readonly maxPendingTiles: number;
  /** The three longest frames, for finding stalls. */
  readonly slowest: readonly FrameSample[];
}

export function summarize(scenario: Scenario, samples: readonly FrameSample[], elapsedMs: number): MeasureResult {
  const intervals = samples.map((s) => s.interval).sort((a, b) => a - b);
  const at = (q: number) => intervals[Math.min(intervals.length - 1, Math.floor(intervals.length * q))] ?? 0;
  const draws = samples.map((s) => s.drawMs).sort((a, b) => a - b);
  const drawAt = (q: number) => draws[Math.min(draws.length - 1, Math.floor(draws.length * q))] ?? 0;
  const round = (v: number) => Math.round(v * 10) / 10;
  return {
    scenario,
    frames: samples.length,
    fps: Math.round((samples.length / (elapsedMs / 1000)) * 10) / 10,
    medianMs: Math.round(at(0.5) * 10) / 10,
    p95Ms: Math.round(at(0.95) * 10) / 10,
    worstMs: Math.round((intervals[intervals.length - 1] ?? 0) * 10) / 10,
    drawMedianMs: round(drawAt(0.5)),
    drawP95Ms: round(drawAt(0.95)),
    drawWorstMs: round(draws[draws.length - 1] ?? 0),
    tilesDrawn: samples.reduce((n, s) => n + s.renderedTiles, 0),
    objectsDrawn: samples.reduce((n, s) => n + s.drawnUnits, 0),
    maxPendingTiles: samples.reduce((n, s) => Math.max(n, s.pendingTiles), 0),
    slowest: [...samples].sort((a, b) => b.interval - a.interval).slice(0, 3),
  };
}
